import { useState, useEffect, useCallback, useRef } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { Paper, Box, TextField, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getOpenAndCloseCash, getOpenAndCloseCashByUser } from '../../services/CashRegisterService';
import CashDetailsModal from './CashDetailsModal';
import { getIcon } from '../../utils/IconHandler';
import TableRefreshButton from '../TableRefreshButton/TableRefreshButton';

const OpenAndCloseCash = ({ forUser }) => {

  const { t } = useTranslation();

  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOpenData, setSelectedOpenData] = useState(null);
  const [selectedCloseData, setSelectedCloseData] = useState(null);

  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  const fetchTransactions = useCallback(async (page, pageSize, search, currentSortModel = sortModel, currentFilterModel = filterModel) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const requestOptions = {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      };
      const data = await (
        forUser
          ? getOpenAndCloseCashByUser(page, pageSize, cleanSearch, requestOptions)
          : getOpenAndCloseCash(page, pageSize, cleanSearch, requestOptions)
      );

      
      // El backend devuelve { cashRegisters: [...], total: number, page: number, pageSize: number }
      if (data && typeof data === 'object') {
        const transactionsData = data.cashRegisters || data.transactions || data.data || [];
        const total = data.total || data.totalCount || 0;
        
        // El backend ya devuelve los datos agrupados con la estructura correcta
        const formattedData = transactionsData.map((t) => ({
          id: t.id,
          branch: t.branch,
          terminal: t.terminal, // ← Agregar el terminal
          user: t.user,
          date: t.date,
          openTime: t.openTime,
          closeTime: t.closeTime,
          sold: t.sold,
          soldLocal: t.soldLocal,
          deposit: t.deposit,
          purchase: t.purchase,
          closeDifference: t.closeDifference,
          reconciliationStatus: t.reconciliationStatus,
          openDifference: t.openDifference,
          openData: t.open,
          closeData: t.close
        }));

        setTransactions(formattedData);
        setTotalRows(total);
      } else {
        // Fallback para compatibilidad con respuesta anterior
        const fallbackData = data.cashRegisters || data.transactions || data.data || data || [];
        const formattedData = (fallbackData || []).map((t) => ({
          id: t.id,
          branch: t.branch,
          terminal: t.terminal, // ← Agregar el terminal
          user: t.user,
          date: t.date,
          openTime: t.openTime,
          closeTime: t.closeTime,
          sold: t.sold,
          soldLocal: t.soldLocal,
          deposit: t.deposit,
          purchase: t.purchase,
          closeDifference: t.closeDifference,
          reconciliationStatus: t.reconciliationStatus,
          openDifference: t.openDifference,
          openData: t.open,
          closeData: t.close
        }));

        setTransactions(formattedData);
        setTotalRows(formattedData.length);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [forUser, sortModel, filterModel]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchTransactions(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchTransactions, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  // Funciones para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);
    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (searchTerm) => {
    setSearchTerm(searchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchTransactions(1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    }, 500);
  };


  const handleOpenModal = (rowData) => {
    // Construir los objetos con la estructura que espera el modal
    const openData = rowData.openData ? {
      ...rowData.openData,
      branch: { name: rowData.branch },
      terminal: { reference: rowData.terminal || 'Terminal no disponible' },
      user: rowData.user,
      formattedDate: rowData.date,
      formattedTime: rowData.openTime,
      deposit: rowData.deposit, // Agregar depósitos
      purchase: rowData.purchase // Agregar compras
    } : null;

    const closeData = rowData.closeData ? {
      ...rowData.closeData,
      branch: { name: rowData.branch },
      terminal: { reference: rowData.terminal || 'Terminal no disponible' },
      user: rowData.user,
      formattedDate: rowData.date,
      formattedTime: rowData.closeTime,
      deposit: rowData.deposit, // Agregar depósitos
      purchase: rowData.purchase // Agregar compras
    } : null;

    setSelectedOpenData(openData);
    setSelectedCloseData(closeData);
    setModalOpen(true);
  };

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <TextField
            label={t('search')}
            placeholder={t('search_by_branch_user_date')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <TableRefreshButton
            loading={loading}
            onClick={() => fetchTransactions(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)}
          />
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1000 }}>
            <DataGrid
              loading={loading}
              rows={transactions}
              columns={[
                { field: 'branch', headerName: t('branch'), flex: 1 },
                { field: 'user', align: 'center', headerAlign: 'center', headerName: t('staff'), flex: 1 },
                { field: 'date', align: 'center', headerAlign: 'center', headerName: t('date'), flex: 1 },
                { field: 'openTime', align: 'center', headerAlign: 'center', headerName: t('open_time'), flex: 1 },
                { field: 'closeTime', align: 'center', headerAlign: 'center', headerName: t('close_time'), flex: 1 },
                { field: 'sold', align: 'center', headerAlign: 'center', headerName: "Ventas totales", flex: 1.2 },
                { field: 'soldLocal', align: 'center', headerAlign: 'center', headerName: "Ventas local", flex: 1.2 },
                { field: 'deposit', align: 'center', headerAlign: 'center', headerName: "Depositos", flex: 1.2 },
                { field: 'purchase', align: 'center', headerAlign: 'center', headerName: "Compras", flex: 1.2 },
                { field: 'closeDifference', align: 'center', headerAlign: 'center', headerName: t('difference'), flex: 1 },
                {
                  field: 'actions',
                  headerName: t('actions'),
                  flex: 0.7,
                  headerAlign: 'right',
                  sortable: false,
                  renderCell: (params) => (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                      <IconButton onClick={() => handleOpenModal(params.row)}>
                        {getIcon('eye')}
                      </IconButton>
                    </Box>
                  ),
                },
              ]}
              getRowId={(row) => row.id}
              pageSizeOptions={[5, 10, 25]}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              filterMode="server"
              paginationMode="server"
              rowCount={totalRows}
              autoHeight
              disableSelectionOnClick
              isRowSelectable={() => false}
              getRowClassName={(params) => {
                // El backend es la única fuente de verdad: conoce ventas, anulaciones
                // y movimientos de caja de todos los medios de pago.
                if (params.row.reconciliationStatus === 'CRITICAL') return 'row-red';
                if (params.row.reconciliationStatus === 'WARNING') return 'row-orange';
                return '';
              }}
              sx={{
                '& .row-red': { backgroundColor: '#ffcccb !important', opacity: 0.8 },
                '& .row-orange': { backgroundColor: '#ffdd99 !important', opacity: 0.8 },
                '& .MuiDataGrid-row:hover': { opacity: 1 },
                '& .Mui-selected': { backgroundColor: 'transparent !important' },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Modal de detalles */}
      <CashDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        openData={selectedOpenData}
        closeData={selectedCloseData}
      />
    </>
  );
};

export default OpenAndCloseCash;
